(() => {
  'use strict';

  const ROOT_SELECTOR = 'lfm-newsletter-popup';
  const BAR_SELECTOR = '[data-lfnp-reward]';
  const STORAGE_PREFIX = 'lfnp:reward:v1:';
  const MAX_CODE_LENGTH = 128;
  const MAX_TERMS_LENGTH = 1000;
  const memory = Object.create(null);
  let refreshFrame = null;
  let expiryTimer = null;

  const getRoots = () => Array.from(document.querySelectorAll(ROOT_SELECTOR));

  const getBar = (root) => root?.querySelector(BAR_SELECTOR) || null;

  const getStorage = () => {
    try {
      return window.sessionStorage;
    } catch (_error) {
      return null;
    }
  };

  const storageKey = (campaign) => `${STORAGE_PREFIX}${encodeURIComponent(String(campaign || '').slice(0, 96))}`;

  const readStored = (campaign) => {
    const key = storageKey(campaign);
    const storage = getStorage();
    let raw = null;
    try {
      raw = storage?.getItem(key) || memory[key] || null;
    } catch (_error) {
      raw = memory[key] || null;
    }
    if (!raw) return null;
    try {
      const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!value || typeof value !== 'object' || value.campaign !== campaign) return null;
      const code = String(value.code || '').trim();
      if (!code || code.length > MAX_CODE_LENGTH) return null;
      return {
        campaign,
        code,
        terms: String(value.terms || '').slice(0, MAX_TERMS_LENGTH),
        savedAt: Number(value.savedAt) || Date.now(),
        closed: value.closed === true,
      };
    } catch (_error) {
      return null;
    }
  };

  const writeStored = (state) => {
    const key = storageKey(state.campaign);
    const value = JSON.stringify({
      campaign: state.campaign,
      code: state.code,
      terms: state.terms,
      savedAt: state.savedAt,
      closed: Boolean(state.closed),
    });
    memory[key] = value;
    try {
      getStorage()?.setItem(key, value);
    } catch (_error) {
    }
  };

  const safeText = (node, limit) => String(node?.textContent || '').trim().slice(0, limit);

  const setHiddenState = (node, hidden) => {
    if (!node) return;
    if (node.hidden !== hidden) node.hidden = hidden;
    const ariaHidden = hidden ? 'true' : 'false';
    if (node.getAttribute('aria-hidden') !== ariaHidden) node.setAttribute('aria-hidden', ariaHidden);
  };

  const isVisible = (node) => {
    if (!node || node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
    if (!node.isConnected) return false;
    const style = window.getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
  };

  const hasBlockingSurface = () => {
    if (document.querySelector('dialog[open]')) return true;
    if (Array.from(document.querySelectorAll('[data-banner]')).some(isVisible)) return true;
    if (document.body?.classList.contains('lfm-cart-open')) return true;
    if (document.querySelector('#lfm-cart-drawer.is-open, #lfm-search-overlay.is-open')) return true;
    return Array.from(document.querySelectorAll(
      '#shopify-pc__banner.shopify-pc__banner__dialog, [role="alertdialog"]'
    )).some(isVisible);
  };

  const readConfig = (root) => {
    const node = root?.querySelector('script[type="application/json"][data-lfnp-config]');
    if (!node) return null;
    try {
      const value = JSON.parse(node.textContent || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    } catch (_error) {
      return null;
    }
  };

  const endDateTimestamp = (value) => {
    const raw = String(value || '').trim();
    if (!raw || !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return null;
    const timestamp = Date.parse(raw);
    return Number.isFinite(timestamp) ? timestamp : null;
  };

  const hasValidEndDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return true;
    const timestamp = endDateTimestamp(raw);
    return timestamp !== null && timestamp > Date.now();
  };

  const stateMatchesRoot = (root, state) => {
    const config = readConfig(root);
    if (!config) return false;
    if (root.querySelector('[data-audience-view]') && config.context?.audience === 'returning') return false;
    const configuredCode = String(config.couponCode || '').trim();
    if (!configuredCode || configuredCode !== state.code) return false;
    return hasValidEndDate(config.endsAt);
  };

  const rootPriority = (root) => {
    const value = Number(root?.dataset?.priority);
    return Number.isFinite(value) ? value : 0;
  };

  const allowedDiscountCode = (code) => /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(code);

  const discountLink = (code) => {
    if (!allowedDiscountCode(code)) return '';
    const rootValue = String(window.Shopify?.routes?.root || '/');
    let rootUrl;
    try {
      rootUrl = new URL(rootValue, window.location.origin);
    } catch (_error) {
      return '';
    }
    if (rootUrl.origin !== window.location.origin) return '';
    const rootPath = rootUrl.pathname.replace(/\/+$/, '');
    const target = new URL(`${rootPath}/discount/${encodeURIComponent(code)}`, window.location.origin);
    let redirectPath = window.location.pathname || '/';
    if (redirectPath.startsWith(`${rootPath}/discount/`) || /^\/discount\//.test(redirectPath)) {
      redirectPath = `${rootPath || ''}/` || '/';
    }
    target.search = `?redirect=${encodeURIComponent(redirectPath)}`;
    return target.origin === window.location.origin ? target.href : '';
  };

  const stateFromSuccess = (root, campaign) => {
    const success = Array.from(root.querySelectorAll('[data-result="success"]')).find((node) => {
      const resultCampaign = node.getAttribute('data-campaign');
      return (!resultCampaign || resultCampaign === campaign) && isVisible(node);
    });
    if (!success) return null;
    const code = safeText(success.querySelector('[data-code]'), MAX_CODE_LENGTH);
    if (!code) return null;
    const terms = safeText(success.querySelector('[data-conditions], .lfnp-conditions'), MAX_TERMS_LENGTH);
    return { campaign, code, terms, savedAt: Date.now(), closed: false };
  };

  const setBarState = (root, state, selected = true) => {
    const bar = getBar(root);
    if (!bar) return;
    const codeNode = bar.querySelector('[data-reward-code]');
    const termsNode = bar.querySelector('[data-reward-terms]');
    const applyNode = bar.querySelector('[data-reward-apply]');
    const statusNode = bar.querySelector('[data-reward-status]');
    if (!state || state.closed || !selected) {
      setHiddenState(bar, true);
      bar.removeAttribute('data-reward-ready');
      return;
    }
    if (codeNode) codeNode.textContent = state.code;
    if (termsNode) {
      const localizedTerms = bar.getAttribute('data-reward-terms') !== null
        ? bar.dataset.rewardTerms || ''
        : state.terms;
      if (termsNode.textContent !== localizedTerms) termsNode.textContent = localizedTerms;
      if (termsNode.hidden !== !localizedTerms) termsNode.hidden = !localizedTerms;
    }
    if (statusNode && statusNode.textContent) statusNode.textContent = '';
    if (applyNode) {
      const href = discountLink(state.code);
      applyNode.hidden = !href;
      if (href) applyNode.setAttribute('href', href);
      else applyNode.removeAttribute('href');
      applyNode.textContent = bar.dataset.rewardApplyLabel || '';
    }
    const copyNode = bar.querySelector('[data-reward-copy]');
    if (copyNode) copyNode.textContent = bar.dataset.rewardCopyLabel || '';
    bar.dataset.rewardReady = 'true';
    const show = !hasBlockingSurface();
    setHiddenState(bar, !show);
  };

  const restore = () => {
    if (expiryTimer !== null) {
      window.clearTimeout?.(expiryTimer);
      expiryTimer = null;
    }
    const roots = getRoots();
    roots.forEach((root) => installBarControls(root));
    const candidates = [];
    roots.forEach((root) => {
      const campaign = String(root.dataset.campaign || '').trim();
      if (!campaign) return;
      const state = readStored(campaign);
      if (!state || state.closed) return;
      if (!stateMatchesRoot(root, state)) {
        writeStored({ ...state, closed: true });
        return;
      }
      if (root.audienceView === 'returning') return;
      candidates.push({ root, state });
    });
    candidates.sort((left, right) =>
      right.state.savedAt - left.state.savedAt ||
      rootPriority(right.root) - rootPriority(left.root) ||
      String(left.state.campaign).localeCompare(String(right.state.campaign))
    );
    const selected = candidates[0];
    if (selected) {
      const expiry = endDateTimestamp(readConfig(selected.root)?.endsAt);
      if (expiry !== null) {
        const delay = Math.min(Math.max(0, expiry - Date.now() + 25), 2147483647);
        if (typeof window.setTimeout === 'function') {
          expiryTimer = window.setTimeout(() => {
            expiryTimer = null;
            scheduleRestore();
          }, delay);
        }
      }
    }
    roots.forEach((root) => {
      const campaign = String(root.dataset.campaign || '').trim();
      const candidate = selected?.root === root ? selected.state : null;
      setBarState(root, candidate, Boolean(candidate && campaign));
    });
  };

  const scheduleRestore = () => {
    if (refreshFrame !== null) return;
    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = null;
      restore();
    });
  };

  const installBarControls = (root) => {
    const bar = getBar(root);
    if (!bar || bar.dataset.rewardBound === 'true') return;
    bar.dataset.rewardBound = 'true';
    const campaign = String(root.dataset.campaign || '').trim();
    const copyButton = bar.querySelector('[data-reward-copy]');
    const applyLink = bar.querySelector('[data-reward-apply]');
    applyLink?.addEventListener('click', () => {
      if (applyLink.getAttribute('href')) root.emitEvent?.('cta_click', { reason: 'reward_apply' });
    });
    copyButton?.addEventListener('click', async () => {
      const state = readStored(campaign);
      const statusNode = bar.querySelector('[data-reward-status]');
      if (!state || !state.code) return;
      let copyTimer;
      copyButton.disabled = true;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await Promise.race([
          navigator.clipboard.writeText(state.code),
          new Promise((_, reject) => {
            copyTimer = window.setTimeout(() => reject(new Error('Clipboard timed out')), 2000);
          }),
        ]);
        root.emitEvent?.('code_copy', { reason: 'reward' });
        if (statusNode) statusNode.textContent = bar.dataset.rewardCopied || '';
      } catch (_error) {
        if (statusNode) statusNode.textContent = bar.dataset.rewardCopyFallback || '';
      } finally {
        window.clearTimeout(copyTimer);
        copyButton.disabled = false;
      }
    });
    bar.querySelector('[data-reward-close-button]')?.addEventListener('click', () => {
      const state = readStored(campaign);
      if (!state) return;
      state.closed = true;
      writeStored(state);
      restore();
    });
  };

  const handleSuccess = (event) => {
    const detail = event?.detail;
    if (!detail || detail.event !== 'subscribe_success') return;
    const campaign = String(detail.campaign || '').trim();
    if (!campaign) return;
    const root = getRoots().find((candidate) => candidate.dataset.campaign === campaign);
    if (!root) return;
    const state = stateFromSuccess(root, campaign);
    if (!state || !stateMatchesRoot(root, state)) return;
    writeStored(state);
    installBarControls(root);
    restore();
  };

  const mutationAffectsSurface = (mutation) => {
    const target = mutation.target;
    if (target?.closest?.(BAR_SELECTOR)) return false;
    if (mutation.type === 'attributes') {
      if (target === document.body) return mutation.attributeName === 'class';
      return Boolean(target?.matches?.(
        'dialog, [data-banner], #lfm-cart-drawer, #lfm-search-overlay, #shopify-pc__banner, [role="alertdialog"]'
      ));
    }
    if (mutation.type !== 'childList') return false;
    return Array.from(mutation.addedNodes || []).concat(Array.from(mutation.removedNodes || [])).some((node) =>
      node?.nodeType === 1 && (node.matches?.(
        'dialog, [data-banner], #lfm-cart-drawer, #lfm-search-overlay, #shopify-pc__banner, [role="alertdialog"]'
      ) || node.querySelector?.(
        'dialog, [data-banner], #lfm-cart-drawer, #lfm-search-overlay, #shopify-pc__banner, [role="alertdialog"]'
      ))
    );
  };

  const init = () => {
    getRoots().forEach((root) => installBarControls(root));
    restore();
    if (window.MutationObserver && document.body) {
      const observer = new MutationObserver((mutations) => {
        if (mutations.some(mutationAffectsSurface)) scheduleRestore();
      });
      observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'open', 'aria-hidden', 'inert'],
      });
    }
    window.addEventListener('pageshow', scheduleRestore, { passive: true });
    window.addEventListener('popstate', scheduleRestore, { passive: true });
  };

  document.addEventListener('lfnp:event', handleSuccess);
  document.addEventListener('lfnp:event', (event) => {
    if (event.detail?.event === 'audience_view') scheduleRestore();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
